import { overlapCheck, TimeFunction } from "@/common/datetime/TimeFunction";
import { useComponentDimensions } from "@/hooks/useWindowDimensions";
import cloneDeep from "lodash/cloneDeep";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { Group, Layer, Line, Stage } from "react-konva";
import {
  EventBufferEnum,
  EventStatusEnum,
  IStatusWrapperProps,
  ITimeLineEvent,
  ITimelineProps,
} from "./Models";
import { AwayStatus } from "./components/AwayStatus";
import { BusyStatus } from "./components/BusyStatus";
import { ClosedStatus } from "./components/ClosedStatus";
import { ConflictStatus } from "./components/ConflictStatus";
import { IStatusProps } from "./components/Models";
import { SelectedStatus } from "./components/SelectedStatus";
import { FreeStatus } from "./components/FreeStatus";
import {
  BufferTimeStatus,
  NoInformationStatus,
} from "./components/StatusTimeline";
import { TentativeStatus } from "./components/TentativeStatus";
import { WorkingElseWhereStatus } from "./components/WorkingElseWhereStatus";

const TimeLine = (props: ITimelineProps) => {
  const [refreshTimeLine, setRefreshTimeLine] = useState<boolean>(false);
  const [canvasSection_ref, width] = useComponentDimensions();

  const state_ref: MutableRefObject<any> = useRef(null);
  const layerRef: MutableRefObject<any> = useRef();

  const strokeWidth = 3;
  const height_default = 24;

  const [widthState, setWidthState] = useState<number>(0);
  const [heightState] = useState<number>(height_default);

  const [_listCoordinates, setListCoordinates] = useState([]);

  useEffect(() => {
    const refreshTimeout = setTimeout(() => {
      if (!refreshTimeLine) {
        setRefreshTimeLine(true);
      }
    }, 100);

    return () => clearTimeout(refreshTimeout);
  }, [refreshTimeLine]);

  useEffect(() => {
    if (width > 0) {
      setWidthState(width);
      getCoordinates(width);
    }
  }, [width]);

  useEffect(() => {
    lineY();
  }, [_listCoordinates]);

  const getCoordinates = (width: number): void => {
    // const _width = (state_ref.current as unknown as any)?.width() || width;
    const widthCanvas = width;
    const _tileSize = widthCanvas / 19;
    const _halfTileSize = _tileSize / 2;
    let _lastTileSize = _halfTileSize;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listCoordinates: any = [];

    for (let index = 0; index < 19; index++) {
      listCoordinates.push([
        _lastTileSize,
        heightState,
        _lastTileSize,
        heightState - 6,
      ]);
      _lastTileSize = _lastTileSize + _tileSize;
    }
    setListCoordinates(listCoordinates);
  };

  const calculateEventCoordinate = (width: number, idx: number): number => {
    const widthCanvas = width || 0;
    const _tileSize = widthCanvas / 19;
    const _halfTileSize = _tileSize / 2;
    return _tileSize * idx + _halfTileSize;
  };

  const calculateTime = (time1: Date, time2: Date): number => {
    return (
      (time1?.getTime() - time2?.getTime() + 3600000) / 1000 / 60 / 30 || 0
    );
  };

  const lineY = () => {
    return _listCoordinates.map((_item: any, index: number) => {
      return (
        <Line
          key={index}
          points={_listCoordinates[index]}
          stroke="#BDBDBD"
          strokeWidth={1.5}
        />
      );
    });
  };

  const TimeLineTypeView = (): JSX.Element => {
    if (!props.isNoInformation) {
      return (
        <NoInformationStatus
          x={-1}
          heightState={heightState}
          widthState={widthState}
          totalTile={0}
          height={15}
          strokeWidth={1.5}
        />
      );
    } else {
      return (
        <>
          {lineY()}
          <Line
            points={[0, height_default, widthState, height_default]}
            stroke="#BDBDBD"
            strokeWidth={strokeWidth}
          />
        </>
      );
    }
  };

  const newEvents = [...(props.events || [])].sort((a, b) => {
    const datetimeA = a.start;
    const datetimeB = b.start;

    if (datetimeA < datetimeB) {
      return -1;
    } else if (datetimeA > datetimeB) {
      return 1;
    } else {
      return 0;
    }
  });

  const eventsSelected = () => {
    return (
      newEvents &&
      newEvents?.map((evt) => {
        if (evt.workspaceInfo && evt.workspaceInfo.remaining > 0) return;

        const _startTimeSelected = new Date(evt.start);
        const _endTimeSelected = new Date(evt.end);

        if (
          !props.isDisplayBufferTime &&
          evt?.preBufferTime !== undefined &&
          evt?.postBufferTime !== undefined
        ) {
          _startTimeSelected.setMinutes(
            _startTimeSelected.getMinutes() - (evt?.preBufferTime || 0)
          );
          _endTimeSelected.setMinutes(
            _endTimeSelected.getMinutes() + (evt?.postBufferTime || 0)
          );
        }

        const startIdx = calculateTime(_startTimeSelected, props.timeSlider);
        const endIdx = calculateTime(_endTimeSelected, props.timeSlider);
        const totalTile = endIdx - startIdx;
        //Tooltip for workspace info
        let toolTip = "";
        if (
          evt.workspaceInfo &&
          props.dateTimeFormat &&
          evt.workspaceInfo.remaining == 0
        ) {
          const from = TimeFunction.dateTimeFormatter(
            evt.start,
            props.dateTimeFormat.timeFormat
          ) as string;
          const to = TimeFunction.dateTimeFormatter(
            evt.end,
            props.dateTimeFormat.timeFormat
          ) as string;
          toolTip =
            from +
            " - " +
            to +
            " " +
            "Remaining" +
            ": " +
            evt.workspaceInfo.remaining;
        } else {
          toolTip =
            TimeFunction.dateTimeFormatter(
              _startTimeSelected,
              props.dateTimeFormat?.timeFormat || ""
            ) +
            " - " +
            TimeFunction.dateTimeFormatter(
              _endTimeSelected,
              props.dateTimeFormat?.timeFormat || ""
            );
        }
        const _props: IStatusProps = {
          x: calculateEventCoordinate(width, startIdx),
          widthState: width || 0,
          heightState: heightState,
          height: 15,
          totalTile: totalTile,
          strokeWidth: 1.5,
          toolTip: toolTip,
          // startTimeSelected: StartTimeSelected,
          // endTimeSelected: EndTimeSelected,
          subject: evt?.subject || "",
          bufferTimeText: props.bufferTimeText,
          minutesText: props.minutesText,
        };
        // const view = GetStatus(evt, _props);
        // return GetStatus(evt, _props, startIdx, endIdx, totalTile);
        return (
          <StatusWrapper
            _props={_props}
            key={evt.id}
            evt={evt}
            startIdx={startIdx}
            endIdx={endIdx}
            totalTile={totalTile}
          />
        );
      })
    );
  };

  const StatusWrapper = ({
    _props,
    evt,
    startIdx,
    endIdx,
    totalTile,
  }: IStatusWrapperProps) => {
    const [isHover, setIsHover] = useState<boolean>(false);

    const onMouseEnter = () => {
      setIsHover(true);
    };

    const onMouseLeave = () => {
      setIsHover(false);
    };

    return (
      <Group onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {GetStatus(evt, _props, startIdx, endIdx, totalTile, isHover)}
        {props.isDisplayBufferTime && (
          <BufferTimeStatus
            key={Math.floor(Math.random() * 100)}
            x={calculateEventCoordinate(widthState, startIdx)}
            heightState={heightState}
            widthState={width || 0}
            totalTile={totalTile}
            height={15}
            strokeWidth={1.5}
            bufferStatus={checkBuffer(
              evt?.preBufferTime || 0,
              evt?.postBufferTime || 0
            )}
            preBufferTime={evt?.preBufferTime}
            postBufferTime={evt?.postBufferTime}
            timeFormat={props.dateTimeFormat?.timeFormat || ""}
            bufferTimeText={props.bufferTimeText}
            minutesText={props.minutesText}
            isHover={isHover}
          />
        )}
      </Group>
    );
  };

  const GetStatus = (
    evt: ITimeLineEvent,
    _props: IStatusProps,
    startIdx: number,
    endIdx: number,
    totalTile: number,
    isHover?: boolean
  ) => {
    let renderContent;
    switch (evt.timelineStatus) {
      case EventStatusEnum.Busy:
        renderContent = (
          <BusyStatus
            key={evt.id}
            {..._props}
            isHover={isHover}
            isAttendee={props.isAttendee}
          />
        );
        break;
      case EventStatusEnum.Tentative:
        renderContent = (
          <TentativeStatus
            key={evt.id}
            {..._props}
            isAttendee={props.isAttendee}
          />
        );
        break;
      case EventStatusEnum.WorkingElsewhere:
        renderContent = (
          <WorkingElseWhereStatus
            key={evt.id}
            {..._props}
            isAttendee={props.isAttendee}
          />
        );
        break;
      case EventStatusEnum.Closed:
        renderContent = (
          <ClosedStatus
            closeTimeText={props.closeTimeText}
            key={evt.id}
            {..._props}
          />
        );
        break;
      case EventStatusEnum.Selected:
        renderContent = <SelectedStatus key={evt.id} {..._props} />;
        break;
      case EventStatusEnum.Away:
        renderContent = (
          <AwayStatus key={evt.id} {..._props} isAttendee={props.isAttendee} />
        );
        break;
      case EventStatusEnum.Free:
        renderContent = (
          <FreeStatus key={evt.id} {..._props} isAttendee={props.isAttendee} />
        );
        break;
      default:
        renderContent = (
          <BusyStatus
            key={evt.id}
            {..._props}
            isHover={isHover}
            isAttendee={props.isAttendee}
          />
        );
        break;
    }

    return renderContent;
  };

  const statusSelected = () => {
    const _startTimeSelected = props.startTimeSelected || new Date();
    const _endTimeSelected = props.endTimeSelected || new Date();

    const _startTimeCheckConflict = props.startTimeSelected || new Date();
    const _endTimeCheckConflict = props.endTimeSelected || new Date();

    if (!props.isDisplayBufferTime && props.buffer) {
      _startTimeSelected.setMinutes(
        _startTimeSelected.getMinutes() - (props.buffer?.preBufferTime || 0)
      );
      _endTimeSelected.setMinutes(
        _endTimeSelected.getMinutes() + (props.buffer?.postBufferTime || 0)
      );
    } else {
      _startTimeCheckConflict.setMinutes(
        _startTimeCheckConflict.getMinutes() -
        (props.buffer?.preBufferTime || 0)
      );
      _endTimeCheckConflict.setMinutes(
        _endTimeCheckConflict.getMinutes() + (props.buffer?.postBufferTime || 0)
      );
    }

    const startIdx = calculateTime(_startTimeSelected, props.timeSlider);
    const endIdx = calculateTime(_endTimeSelected, props.timeSlider);
    const totalTile = endIdx - startIdx;

    const timeTooltip =
      TimeFunction.dateTimeFormatter(
        _startTimeSelected,
        props.dateTimeFormat?.timeFormat || ""
      ) +
      " - " +
      TimeFunction.dateTimeFormatter(
        _endTimeSelected,
        props.dateTimeFormat?.timeFormat || ""
      );

    const isHaveBuffer =
      (props.buffer?.preBufferTime !== null &&
        props.buffer?.preBufferTime !== undefined &&
        props.buffer?.preBufferTime !== 0) ||
      (props.buffer?.postBufferTime !== null &&
        props.buffer?.postBufferTime !== undefined &&
        props.buffer?.postBufferTime !== 0);

    const eventClone = cloneDeep(props.events);

    //let isWSdraw: boolean = false;
    let isCurrentAppointment = false;

    const checkDuplicate = eventClone?.map((evt: ITimeLineEvent) => {
      // if (evt.workspaceInfo && evt.workspaceInfo.remaining == 0) {
      //   isWSdraw = true;
      // }
      if (evt.isCurrentAppointment) {
        isCurrentAppointment = true;
      }

      const startEvent = evt.start;
      const EndEvent = evt.end;
      if (
        props.isDisplayBufferTime &&
        evt?.preBufferTime !== undefined &&
        evt?.postBufferTime !== undefined
      ) {
        startEvent.setMinutes(
          startEvent.getMinutes() - (evt?.preBufferTime || 0)
        );
        EndEvent.setMinutes(EndEvent.getMinutes() + (evt?.postBufferTime || 0));
      }
      return overlapCheck(
        _startTimeSelected,
        _endTimeSelected,
        startEvent,
        EndEvent
      );
    });
    const check = checkDuplicate?.findIndex((a: boolean) => a);

    let status = <></>;

    let isConflict = check !== -1;
    if (isConflict) {
      isConflict = !props.exchangePropertyResource?.isAllowConflict;
    }
    const _isConflict = isConflict;

    const overlappingEvents = eventClone?.filter((evt: ITimeLineEvent) => {
      const startEvent = new Date(evt.start);
      const endEvent = new Date(evt.end);

      return (_startTimeSelected < endEvent && _endTimeSelected > startEvent);
    }).map(evt => ({
      start: evt.start,
      end: evt.end,
      subject: evt.subject || '' // Provide a default empty string
    }));

    if (!_isConflict) {
      status = (
        <SelectedStatus
          toolTip={timeTooltip}
          x={calculateEventCoordinate(width, startIdx)}
          heightState={heightState}
          widthState={width || 0}
          totalTile={totalTile}
          height={15}
          strokeWidth={1.5}
          bufferStatus={checkBuffer(
            props.buffer?.preBufferTime || 0,
            props.buffer?.postBufferTime || 0
          )}
          isHaveBuffer={isHaveBuffer}
        />
      );
    } else {
      if (isCurrentAppointment) {
        status = <></>;
      } else {
        status = (
          <ConflictStatus
            toolTip={timeTooltip}
            x={calculateEventCoordinate(width, startIdx)}
            heightState={heightState}
            widthState={width || 0}
            totalTile={totalTile}
            height={15}
            strokeWidth={1.5}
            bufferStatus={checkBuffer(
              props.buffer?.preBufferTime || 0,
              props.buffer?.postBufferTime || 0
            )}
            isHaveBuffer={isHaveBuffer}
            isSelected={true}
            overlappingEvents={overlappingEvents}
          />
        );
      }
    }
    return (
      <>
        <Group>
          {status}
          {props.isDisplayBufferTime && isHaveBuffer && (
            <BufferTimeStatus
              // startTimeSelected={StartTimeSelected}
              // endTimeSelected={EndTimeSelected}
              key={"bufferSelected"}
              x={calculateEventCoordinate(widthState, startIdx)}
              heightState={heightState}
              widthState={width || 0}
              totalTile={totalTile}
              height={15}
              strokeWidth={1.5}
              bufferStatus={checkBuffer(
                props.buffer?.preBufferTime || 0,
                props.buffer?.postBufferTime || 0
              )}
              preBufferTime={props.buffer?.preBufferTime}
              postBufferTime={props.buffer?.postBufferTime}
              timeFormat={props.dateTimeFormat?.timeFormat || ""}
              bufferTimeText={props.bufferTimeText}
              minutesText={props.minutesText}
              isSelected={true}
              isConflict={_isConflict}
            />
          )}
        </Group>
      </>
    );
  };

  const checkBuffer = (pre: number, post: number) => {
    if (pre > 0 && post > 0) {
      return EventBufferEnum.Buffer;
    }

    if (pre > 0 && post === 0) {
      return EventBufferEnum.BufferStart;
    }
    if (pre === 0 && post > 0) {
      return EventBufferEnum.BufferEnd;
    }
    return EventBufferEnum.NoneBuffer;
  };

  return (
    <div ref={canvasSection_ref} id="canvas-section">
      <Stage ref={state_ref} width={widthState || 0} height={heightState || 0}>
        <Layer ref={layerRef}>
          {TimeLineTypeView()}
          {/* <Group opacity={0.8}> */}
          {eventsSelected()}
          {statusSelected()}
          {/* </Group> */}
        </Layer>
      </Stage>
    </div>
  );
};

export default TimeLine;
